import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, Send } from "lucide-react";
import BrandMark from "../components/BrandMark";

export default function Review() {
  const nav = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const founder = JSON.parse(sessionStorage.getItem("srd_founder") || "null");
  const consent = JSON.parse(sessionStorage.getItem("srd_consent") || "null");
  const answers = JSON.parse(sessionStorage.getItem("srd_answers") || "{}");
  const qType = sessionStorage.getItem("srd_questionnaire_type") || "mvp";

  useEffect(() => {
    api.get(`/api/questionnaire?type=${qType}`).then((r) => setQuestionnaire(r.data));
  }, [qType]);

  useEffect(() => {
    if (!founder || !consent) {
      nav("/");
    }
  }, [founder, consent, nav]);

  if (!questionnaire) return <div className="min-h-screen flex items-center justify-center label-mono">Загрузка…</div>;

  const labelFor = (q, value) => {
    if (q.type === "long_text") return value;
    if (q.type === "multiple_choice" && Array.isArray(value))
      return value.map((v) => q.options.find((o) => o.value === v)?.label || v).join(", ");
    return q.options.find((o) => o.value === value)?.label || value;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/api/submissions", {
        questionnaire_type: qType,
        questionnaire_version: questionnaire.version,
        founder,
        answers,
        consent: { ...consent, accepted_at: new Date().toISOString() },
      });
      sessionStorage.setItem("srd_submission_id", data.submission_id);
      sessionStorage.setItem("srd_share_token", data.share_token);
      nav(`/deck/${data.submission_id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="border-b border-black/10">
        <div className="max-w-screen-xl mx-auto px-8 py-5 flex items-center justify-between">
          <BrandMark />
          <div className="label-mono">ШАГ 03 / 03 · ПРОВЕРКА</div>
        </div>
      </div>
      <main className="flex-1">
        <div className="max-w-screen-xl mx-auto px-8 py-16 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <h1 className="text-4xl md:text-5xl font-medium tracking-tight">Проверьте ответы</h1>
            <p className="mt-4 text-foreground/70 leading-relaxed">
              Можно вернуться и&nbsp;поправить любой блок. После отправки вы&nbsp;получите 3&nbsp;индекса,
              профиль, сильные стороны, зоны роста и&nbsp;5–7&nbsp;рекомендаций.
            </p>
            <div className="mt-10 flex flex-col gap-3">
              <button className="btn-secondary" onClick={() => nav("/questionnaire")} data-testid="review-back-btn">
                <ArrowLeft size={16} /> Вернуться к вопросам
              </button>
              <button
                className="btn-primary"
                onClick={submit}
                disabled={submitting}
                data-testid="review-submit-btn"
              >
                {submitting ? "Отправка…" : <>Отправить <Send size={16} /></>}
              </button>
              {error && <p className="text-destructive label-mono">{error}</p>}
            </div>
          </div>
          <div className="md:col-span-8 space-y-8">
            <div>
              <div className="label-mono mb-2">КОНТАКТ</div>
              <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
                <div className="text-foreground/60">Стартап</div><div>{founder?.startup_name}</div>
                <div className="text-foreground/60">Сайт</div><div>{founder?.website}</div>
                <div className="text-foreground/60">Контакт</div><div>{founder?.founder_name}</div>
                <div className="text-foreground/60">E-mail</div><div>{founder?.email}</div>
                {founder?.telegram && (<><div className="text-foreground/60">Telegram</div><div>{founder.telegram}</div></>)}
                {founder?.phone && (<><div className="text-foreground/60">Телефон</div><div>{founder.phone}</div></>)}
                {founder?.city && (<><div className="text-foreground/60">Город</div><div>{founder.city}</div></>)}
              </div>
            </div>
            <div className="space-y-4">
              {questionnaire.questions.map((q) => {
                const v = answers[q.id];
                return (
                  <div key={q.id} className="border-t border-black/10 pt-3">
                    <div className="label-mono">{q.id.replace(/^q0?/, "Q")} · {q.section}</div>
                    <div className="text-sm font-medium mt-1">{q.question}</div>
                    <div className="text-sm text-foreground/80 mt-1">
                      {v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)
                        ? <span className="text-destructive">— не отвечен —</span>
                        : <span data-testid={`review-answer-${q.id}`}>{labelFor(q, v)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
